/**
 * AdminBudgetStatsWidget
 *
 * Mounted on /admin/p1. Calls /api/admin/p1/budget-stats which
 * is admin-protected (require_role("admin")) and 5-min cached.
 *
 * Shows median + p25–p75 + sample size n, grouped by category × country
 * × currency, for the last 30 days. Demo data is excluded by default;
 * a toggle includes it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/lib/context';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('keikka_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmt = (n, ccy) => `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })} ${ccy}`;

export const AdminBudgetStatsWidget = ({ days = 30 }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [includeDemo, setIncludeDemo] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/p1/budget-stats`, {
        headers: authHeaders(),
        params: { include_demo: includeDemo, days },
      });
      setData(res.data);
    } catch (e) {
      setData({ rows: [], totals: { requests_with_budget: 0, requests_total: 0 }, error: true });
    } finally {
      setLoading(false);
    }
  }, [includeDemo, days]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const totals = data?.totals || { requests_with_budget: 0, requests_total: 0 };

  return (
    <Card data-testid="admin-budget-widget" className="border-slate-200">
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
        <div className="space-y-1">
          <CardTitle className="text-base flex items-center gap-2">
            <Wallet className="h-4 w-4 text-slate-500" />
            {t('adminP1.budget.title')}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {t('adminP1.budget.subtitle', { days })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <span>{t('adminP1.budget.includeDemo')}</span>
            <Switch
              checked={includeDemo}
              onCheckedChange={setIncludeDemo}
              data-testid="admin-budget-include-demo"
            />
          </label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="admin-budget-refresh"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            <span className="ml-1.5">{t('adminP1.budget.refresh')}</span>
          </Button>
          {data && (
            <Badge
              variant="outline"
              className={data.cached ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}
              data-testid="admin-budget-cache-state"
            >
              {data.cached ? t('adminP1.budget.cached') : t('adminP1.budget.live')}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="text-xs text-slate-500 mb-2" data-testid="admin-budget-totals">
          {t('adminP1.budget.totalRequests', { n: totals.requests_with_budget, total: totals.requests_total })}
        </div>

        {rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-muted-foreground"
               data-testid="admin-budget-empty">
            {t('adminP1.budget.empty')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 border-b">
                <tr>
                  <th className="text-left py-2 font-medium">{t('adminP1.budget.col.category')}</th>
                  <th className="text-left py-2 font-medium">{t('adminP1.budget.col.country')}</th>
                  <th className="text-left py-2 font-medium">{t('adminP1.budget.col.currency')}</th>
                  <th className="text-right py-2 font-medium">{t('adminP1.budget.col.n')}</th>
                  <th className="text-right py-2 font-medium">{t('adminP1.budget.col.median')}</th>
                  <th className="text-right py-2 font-medium">{t('adminP1.budget.col.range')}</th>
                  <th className="text-right py-2 font-medium">{t('adminP1.budget.col.minMax')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.category}-${r.country}-${r.currency}`}
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`admin-budget-row-${i}`}>
                    <td className="py-2">
                      <span className="font-medium">{t(`categories.${r.category}`, { defaultValue: r.category })}</span>
                    </td>
                    <td className="py-2 text-slate-600">{r.country}</td>
                    <td className="py-2 text-slate-600">{r.currency}</td>
                    <td className="py-2 text-right tabular-nums">{r.n}</td>
                    <td className="py-2 text-right font-medium tabular-nums">{fmt(r.median, r.currency)}</td>
                    <td className="py-2 text-right text-slate-600 tabular-nums">{fmt(r.p25, r.currency)} – {fmt(r.p75, r.currency)}</td>
                    <td className="py-2 text-right text-slate-500 tabular-nums">{fmt(r.min, r.currency)} – {fmt(r.max, r.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminBudgetStatsWidget;
